import { Directive, Inject, DOCUMENT } from '@angular/core';

import { ToastrService } from 'ngx-toastr';

@Directive({
    selector: '[appCopy]',
    standalone: true
})
export class CopyDirective {

  constructor(
    private toastr: ToastrService,
    @Inject(DOCUMENT) private document: Document,
  ) { }

  /**
   * Copies `content` to the clipboard and reports the real outcome.
   *
   * @param content - the value to place on the clipboard
   * @returns whether the content actually reached the clipboard
   */
  async copy(content: any): Promise<boolean> {
    const text = content === null || content === undefined ? '' : String(content);
    if (!text) return false;

    const copied = await this.writeViaClipboardApi(text) || this.writeViaExecCommand(text);

    if (copied) this.toastr.success('Copied successfully.');
    else this.toastr.error('Could not copy to clipboard.');
    return copied;
  }

  /**
   * Preferred path. Unlike the execCommand fallback it depends on neither focus
   * nor a DOM selection, so a modal's focus trap cannot interfere with it.
   * Unavailable on insecure origins and refusable by permissions policy, hence
   * the boolean rather than a throw.
   */
  private async writeViaClipboardApi(text: string): Promise<boolean> {
    try {
      if (!navigator?.clipboard?.writeText) return false;
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Fallback for insecure origins and older browsers.
   *
   * The textarea is appended to the open modal rather than to body: Bootstrap's
   * FocusTrap listens for focusin on the document and, seeing focus land outside
   * the modal, immediately pulls it back in — which clears the selection before
   * execCommand runs and makes the copy silently do nothing. Keeping the element
   * inside the trap element avoids that. It is created and removed per call, so
   * it can never be reused from a modal that has since been hidden.
   */
  private writeViaExecCommand(text: string): boolean {
    const container = this.document.querySelector<HTMLElement>('.modal.show') ?? this.document.body;
    const textArea = this.document.createElement('textarea');
    textArea.value = text;
    textArea.setAttribute('readonly', '');
    textArea.setAttribute('aria-hidden', 'true');
    // Keep it off-screen and non-scrolling rather than display:none, which would
    // make it unselectable.
    textArea.style.position = 'fixed';
    textArea.style.top = '0';
    textArea.style.left = '0';
    textArea.style.opacity = '0';
    textArea.style.pointerEvents = 'none';

    container.appendChild(textArea);
    try {
      textArea.focus();
      textArea.select();
      textArea.setSelectionRange(0, text.length);
      return this.document.execCommand('copy');
    } catch {
      return false;
    } finally {
      container.removeChild(textArea);
    }
  }

}
